/**
 * Left navigation for the trainer portal.
 *
 * Visual language matches the tenant `kisiwa_management_dash` and the
 * `admin_dashboard` sidebars — dark navy shell, gradient brand chip,
 * collapsible on desktop, slide-in on mobile, hover tooltips when
 * compact.
 *
 * The trainer surface is deliberately small (Library / Activity /
 * Profile), so items are flat — no groups, no nested sections. That
 * makes the sidebar visually restful compared to the admin console.
 */
import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, BookOpen, ChevronLeft, ChevronRight, HelpCircle, Library,
  LogOut, Sparkles, User, X,
  // The Sparkles import stays because it's used in the brand chip below.
} from 'lucide-react';
import { useAiTrainerAuth } from '@/contexts/AiTrainerAuthContext';

interface SidebarProps {
  isOpen: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onClose: () => void;
}

interface NavItem {
  id: string;
  label: string;
  to: string;
  icon: React.ReactNode;
}

const SIDEBAR_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap');

  .ts-aside {
    position: fixed; left: 0; top: 0; height: 100%;
    display: flex; flex-direction: column;
    background: #0c1929;
    border-right: 1px solid rgba(255,255,255,0.06);
    box-shadow: 4px 0 24px rgba(0,0,0,0.25);
    z-index: 50;
    transition: width 0.28s cubic-bezier(0.4,0,0.2,1),
                transform 0.28s cubic-bezier(0.4,0,0.2,1);
    font-family: 'DM Sans', sans-serif;
    will-change: width;
  }
  .ts-aside.open   { transform: translateX(0); }
  .ts-aside.closed { transform: translateX(-100%); }
  @media(min-width:1024px){ .ts-aside.closed { transform: translateX(0); } }
  .ts-aside.expanded { width: 240px; }
  .ts-aside.compact  { width: 68px; }

  .ts-aside::before {
    content: ''; position: absolute; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px);
    background-size: 32px 32px;
  }

  .ts-backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
    z-index: 40; animation: tsFadeIn 0.2s ease;
  }
  @keyframes tsFadeIn { from{opacity:0} to{opacity:1} }

  .ts-header {
    position: relative; z-index: 1;
    padding: 18px 14px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.07);
    background: rgba(255,255,255,0.02);
    flex-shrink: 0;
  }
  .ts-brand-row {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
  }
  .ts-brand { display: flex; align-items: center; gap: 10px; min-width: 0; overflow: hidden; }
  .ts-logo {
    width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
    background: linear-gradient(135deg, #1a5276, #0e9172);
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 4px 12px rgba(14,145,114,0.35);
  }
  .ts-brand-text { min-width: 0; overflow: hidden; }
  .ts-brand-name {
    font-family: 'Instrument Serif', serif;
    font-size: 15px; font-weight: 400; color: #fff;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    line-height: 1.2;
  }
  .ts-brand-name em { font-style: italic; color: #5ecdb0; }
  .ts-brand-sub {
    font-size: 10.5px; color: rgba(255,255,255,0.35);
    font-weight: 500; letter-spacing: 0.04em;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ts-collapse-btn {
    width: 28px; height: 28px; border-radius: 7px; border: none; flex-shrink: 0;
    background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.5);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .ts-collapse-btn:hover { background: rgba(255,255,255,0.13); color: #fff; }

  .ts-nav {
    flex: 1; overflow-y: auto; overflow-x: hidden;
    padding: 12px 8px; position: relative; z-index: 1;
  }
  .ts-nav::-webkit-scrollbar { width: 4px; }
  .ts-nav::-webkit-scrollbar-track { background: transparent; }
  .ts-nav::-webkit-scrollbar-thumb { background: rgba(26,82,118,0.5); border-radius: 99px; }

  .ts-group-label {
    font-size: 10px; font-weight: 700; letter-spacing: 0.1em;
    text-transform: uppercase; color: rgba(255,255,255,0.2);
    padding: 10px 10px 4px;
  }

  .ts-item {
    width: 100%; display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 10px; border: none;
    background: transparent; cursor: pointer; text-align: left;
    transition: all 0.18s; position: relative; overflow: hidden;
    margin-bottom: 2px; text-decoration: none;
  }
  .ts-item:hover { background: rgba(255,255,255,0.06); }
  .ts-item.active {
    background: rgba(26,82,118,0.3);
    border: 1px solid rgba(26,82,118,0.4);
  }
  .ts-item.active::before {
    content: ''; position: absolute; left: 0; top: 20%; bottom: 20%;
    width: 3px; border-radius: 0 3px 3px 0;
    background: linear-gradient(180deg, #1a5276, #0e9172);
  }
  .ts-item-icon {
    width: 30px; height: 30px; border-radius: 7px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.45);
    transition: all 0.18s;
  }
  .ts-item:hover .ts-item-icon { background: rgba(255,255,255,0.11); color: rgba(255,255,255,0.75); }
  .ts-item.active .ts-item-icon {
    background: linear-gradient(135deg, #1a5276, #0e9172);
    color: #fff; box-shadow: 0 3px 10px rgba(14,145,114,0.35);
  }
  .ts-item-label {
    font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.55);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    transition: color 0.15s; flex: 1; min-width: 0;
  }
  .ts-item:hover .ts-item-label { color: rgba(255,255,255,0.9); }
  .ts-item.active .ts-item-label { color: #fff; font-weight: 600; }

  .ts-tooltip-wrap { position: relative; }
  .ts-tooltip {
    position: absolute; left: calc(100% + 10px); top: 50%;
    transform: translateY(-50%);
    background: #1e3a52; color: #fff; font-size: 12px; font-weight: 600;
    padding: 5px 10px; border-radius: 8px; white-space: nowrap;
    border: 1px solid rgba(255,255,255,0.1);
    box-shadow: 0 4px 16px rgba(0,0,0,0.3);
    pointer-events: none; opacity: 0; transition: opacity 0.15s;
    z-index: 100;
  }
  .ts-tooltip-wrap:hover .ts-tooltip { opacity: 1; }

  .ts-mobile-close {
    width: 28px; height: 28px; border-radius: 7px; border: none; flex-shrink: 0;
    background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.5);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: all 0.15s;
  }
  .ts-mobile-close:hover { background: rgba(255,255,255,0.13); color: #fff; }

  .ts-footer {
    padding: 10px 8px; border-top: 1px solid rgba(255,255,255,0.07);
    background: rgba(0,0,0,0.15); flex-shrink: 0; position: relative; z-index: 1;
  }
  .ts-user-pill {
    display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 10px; margin-bottom: 6px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
  }
  .ts-user-avatar {
    width: 30px; height: 30px; border-radius: 7px; flex-shrink: 0;
    background: linear-gradient(135deg, #1a5276, #0e9172);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Instrument Serif', serif; font-size: 14px; color: #fff;
  }
  .ts-user-name {
    font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,0.85);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ts-user-role { font-size: 10.5px; color: rgba(255,255,255,0.35); white-space: nowrap; }
  .ts-status-dot {
    width: 7px; height: 7px; border-radius: 50%;
    flex-shrink: 0; margin-left: auto;
    animation: tsPulse 2s infinite;
  }
  .ts-status-dot.active   { background: #0e9172; box-shadow: 0 0 6px rgba(14,145,114,0.6); }
  .ts-status-dot.pending  { background: #f59e0b; box-shadow: 0 0 6px rgba(245,158,11,0.6); }
  .ts-status-dot.disabled { background: #dc2626; box-shadow: 0 0 6px rgba(220,38,38,0.6); }
  @keyframes tsPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }

  .ts-footer-btn {
    width: 100%; display: flex; align-items: center; gap: 10px;
    padding: 8px 10px; border-radius: 10px; border: none;
    background: transparent; cursor: pointer; transition: all 0.15s;
    text-align: left; margin-bottom: 2px;
  }
  .ts-footer-btn:hover { background: rgba(255,255,255,0.07); }
  .ts-footer-icon {
    width: 30px; height: 30px; border-radius: 7px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.45);
    transition: all 0.15s;
  }
  .ts-footer-btn:hover .ts-footer-icon { background: rgba(255,255,255,0.12); color: rgba(255,255,255,0.8); }
  .ts-footer-label {
    font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.55);
    transition: color 0.15s; white-space: nowrap;
  }
  .ts-footer-btn:hover .ts-footer-label { color: rgba(255,255,255,0.95); }
  .ts-logout-btn { color: rgba(255,100,100,0.7); }
  .ts-logout-btn .ts-footer-icon { color: rgba(255,100,100,0.7); }
  .ts-logout-btn:hover { background: rgba(220,50,50,0.1); }
  .ts-logout-btn:hover .ts-footer-icon { background: rgba(220,50,50,0.2); color: #ff8080; }
  .ts-logout-btn:hover .ts-footer-label { color: #ff8080; }
`;

const NAV: NavItem[] = [
  { id: 'library',    label: 'Library',    to: '/library',    icon: <Library size={15} /> },
  { id: 'curriculum', label: 'Curriculum', to: '/curriculum', icon: <BookOpen size={15} /> },
  { id: 'activity',   label: 'Activity',   to: '/activity',   icon: <Activity size={15} /> },
];

const TrainerSidebar: React.FC<SidebarProps> = ({
  isOpen, collapsed, onToggleCollapse, onClose,
}) => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, logout } = useAiTrainerAuth();

  // Close the mobile drawer on route change.
  useEffect(() => { onClose(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const initials = (user?.full_name || user?.email || 'T')
    .split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <style>{SIDEBAR_CSS}</style>

      {isOpen && <div className="ts-backdrop lg:hidden" onClick={onClose} />}

      <aside
        className={`ts-aside ${isOpen ? 'open' : 'closed'} ${collapsed ? 'compact' : 'expanded'}`}
        aria-label="Trainer navigation"
      >
        {/* Header */}
        <div className="ts-header">
          <div className="ts-brand-row">
            <div className="ts-brand">
              <div className="ts-logo">
                <Sparkles size={16} color="#fff" />
              </div>
              {!collapsed && (
                <div className="ts-brand-text">
                  <div className="ts-brand-name">Ratiba<em>Edu</em></div>
                  <div className="ts-brand-sub">Trainer portal</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="ts-mobile-close lg:!hidden"
                onClick={onClose}
                aria-label="Close navigation"
              >
                <X size={14} />
              </button>
              <button
                className="ts-collapse-btn hidden lg:!flex"
                onClick={onToggleCollapse}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
              </button>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="ts-nav">
          {!collapsed && <div className="ts-group-label">Corpus</div>}
          {NAV.map((item) => {
            const isActive = location.pathname === item.to
              || (item.to !== '/' && location.pathname.startsWith(item.to));
            const btn = (
              <NavLink
                to={item.to}
                key={item.id}
                className={`ts-item${isActive ? ' active' : ''}`}
              >
                <div className="ts-item-icon">{item.icon}</div>
                {!collapsed && <span className="ts-item-label">{item.label}</span>}
              </NavLink>
            );
            return collapsed ? (
              <div key={item.id} className="ts-tooltip-wrap">
                {btn}
                <div className="ts-tooltip">{item.label}</div>
              </div>
            ) : btn;
          })}
        </nav>

        {/* Footer */}
        <div className="ts-footer">
          {!collapsed && user && (
            <div className="ts-user-pill">
              <div className="ts-user-avatar">{initials}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="ts-user-name">{user.full_name || user.email}</div>
                <div className="ts-user-role" style={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  {user.status === 'active' ? 'Trainer' : user.status}
                </div>
              </div>
              <div className={`ts-status-dot ${user.status}`} title={user.status} />
            </div>
          )}

          <button className="ts-footer-btn" onClick={() => navigate('/profile')}>
            {collapsed ? (
              <div className="ts-tooltip-wrap" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className="ts-footer-icon" style={{ margin: '0 auto' }}><User size={14} /></div>
                <div className="ts-tooltip">Profile</div>
              </div>
            ) : (
              <>
                <div className="ts-footer-icon"><User size={14} /></div>
                <span className="ts-footer-label">Profile</span>
              </>
            )}
          </button>

          <button className="ts-footer-btn" onClick={() => navigate('/help')}>
            {collapsed ? (
              <div className="ts-tooltip-wrap" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className="ts-footer-icon" style={{ margin: '0 auto' }}><HelpCircle size={14} /></div>
                <div className="ts-tooltip">Help</div>
              </div>
            ) : (
              <>
                <div className="ts-footer-icon"><HelpCircle size={14} /></div>
                <span className="ts-footer-label">Help</span>
              </>
            )}
          </button>

          <button className="ts-footer-btn ts-logout-btn" onClick={handleLogout}>
            {collapsed ? (
              <div className="ts-tooltip-wrap" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className="ts-footer-icon" style={{ margin: '0 auto' }}><LogOut size={14} /></div>
                <div className="ts-tooltip">Sign Out</div>
              </div>
            ) : (
              <>
                <div className="ts-footer-icon"><LogOut size={14} /></div>
                <span className="ts-footer-label">Sign Out</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default TrainerSidebar;

/* Layout wrapper — mounts the sidebar + a mobile menu button and
 * gives the outlet the correct margin depending on collapse state. */
export const TrainerLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed,  setCollapsed]  = useState(false);

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <TrainerSidebar
        isOpen={mobileOpen}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        onClose={() => setMobileOpen(false)}
      />

      {/* Mobile pill — opens the drawer on small screens */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-30 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#0c1929] text-white shadow-lg border border-white/10"
        aria-label="Open navigation"
      >
        <Library className="w-4 h-4" />
        <span className="text-xs font-semibold">Menu</span>
      </button>

      <div
        className={`flex-1 flex flex-col min-w-0 transition-[margin] duration-300 ${
          collapsed ? 'lg:ml-[68px]' : 'lg:ml-[240px]'
        }`}
      >
        {children}
      </div>
    </div>
  );
};
